import React, { useEffect, useState } from 'react';

import moment from 'moment-timezone';

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { DecisionIcon } from '../../common/components/DecisionIcon';
import { Loading } from '../../common/components/Loading';
import { FlexRow, TitleSmall } from '../../common/components/v2';
import {
  DecisionType,
  transformRateClass,
} from '../../common/constants/decision';
import { Resource } from '../../common/constants/resource';
import { ProductFactory } from '../../common/productConfig/productFactory';
import { fetchDecision } from './helpers';
import styles from './styles.module.scss';

/**
 * From our PRD the logic is:
 * if rating of null || 0 || 100% then no table rating
 * if over 100 e.g. 125% then +25%, if 150% then +50% and so on.
 */
const getDebits = (percentageAddition: string | undefined) => {
  const rating: number | null = percentageAddition
    ? parseInt(percentageAddition, 10)
    : null;
  if (rating === null || rating === 0) {
    return Resource.NORA_M_DASH;
  } else if (rating >= 100) {
    return (rating - 100).toString();
  } else {
    // Shouldn't happen as we're not supposed to be < 100 but ¯\_(ツ)_/¯
    return Resource.NORA_M_DASH;
  }
};

/**
 * Approved application - OTAF Reason and Amendment in the drop down.
 * Declined application - Decline Reason and Amendment in the drop down
 * Withdrawn application - won't actually have the drop down - it wouldn't
 * have a reason or an amendment
 *
 * Modal content:
 * Decline and OTAF Reasons have title, reasons bullets, and description
 * Amendment will just have the description
 */

// TODO: this should either be done on server or it should return a `displayText` or something
const transformTobaccoClass = (tobaccoClass: string) => {
  return tobaccoClass === 'NonTobacco' ? 'Non-Tobacco' : tobaccoClass;
};

interface DecisionData {
  decisionType: string;
  rateClass: string;
  decision: JSX.Element;
  coverage: string;
  tobaccoClass: string;
  tableRating: string;
  apta: string | number;
  debits: string | undefined;
  createdAt: string;
  percentageAddition: string | undefined;
  isAutoDecision: boolean | undefined;
}

const transformDecisionType = (decisionType: string) => {
  if (decisionType === 'SoftDecline') {
    return 'Soft Decision Checkpoint';
  }
  return decisionType;
};

const getDebitsDisplay = (debits: number, debitsFromPA: string): string => {
  const debitsFromPAInt =
    debitsFromPA !== Resource.NORA_M_DASH ? parseInt(debitsFromPA, 10) : 0;
  const debitsDisplay = Number.isFinite(debits) ? debits : debitsFromPAInt;

  // Handle NaN case properly - if debits is not finite, compare the resolved values
  const shouldShowBothValues = Number.isFinite(debits)
    ? debitsFromPAInt !== debits
    : debitsFromPAInt !== debitsDisplay;

  if (shouldShowBothValues) {
    return `${debitsDisplay} (${debitsFromPA})`;
  }
  return debitsDisplay.toString();
};

const formatData = (decisions: any[]): DecisionData[] => {
  return decisions.map(decision => {
    const debitsFromPA = getDebits(decision.percentageAddition);
    const debits = decision.debits ? decision.debits : 0;

    return {
      decisionType: transformDecisionType(decision.decisionType),
      rateClass: transformRateClass(decision.rateClass),
      decision: <DecisionIcon decision={decision.decision} />,
      coverage: decision.maxCoverage
        ? decision.maxCoverage
        : Resource.NORA_M_DASH,
      tobaccoClass: transformTobaccoClass(decision.tobaccoClass),
      // have to explicitly check for zero before checking for falsy here
      apta:
        decision.apta === 0 || decision.apta
          ? decision.apta
          : Resource.NORA_M_DASH,

      debits:
        decision.decisionType !== DecisionType.AF
          ? getDebitsDisplay(debits, debitsFromPA)
          : Number.isFinite(debits)
            ? debits
            : getDebits(decision.tableRating),

      percentageAddition: Number.isFinite(decision.percentageAddition)
        ? decision.percentageAddition
        : Resource.NORA_M_DASH,
      createdAt: moment(decision.createdAt)
        .tz(moment.tz.guess())
        .format('MMM DD, YYYY · HH:mm:ss z'),
    } as DecisionData;
  });
};

export const Decision = ({
  policyId,
  product,
  carrier,
  subCategory,
  setDecisionsForRuleResult,
}: {
  policyId: string;
  product: string;
  carrier: string;
  subCategory: string;
  setDecisionsForRuleResult: (decisions: any[]) => void;
}) => {
  const [decisionData, setDecisionData] = useState<
    DecisionData[] | null | undefined
  >(undefined);

  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );

  useEffect(() => {
    fetchDecision(product, carrier, policyId).then(data => {
      if (data !== null) {
        data = data.reverse();
        setDecisionData(formatData(data));
        setDecisionsForRuleResult(data);
      } else {
        setDecisionData(null);
      }
    });
  }, []);
  const sectionHead = (
    <FlexRow className={styles.Title}>
      <TitleSmall>{Resource.TASK_TITLE_DECISION}</TitleSmall>
    </FlexRow>
  );

  if (decisionData === undefined) {
    return (
      <div>
        {sectionHead}
        <Loading fullscreen={false} />
      </div>
    );
  } else if (decisionData === null) {
    return (
      <div>
        {sectionHead}
        <Box>
          <Typography variant="body1">Failed to fetch decision data</Typography>
        </Box>
      </div>
    );
  }
  return (
    <div>
      {sectionHead}
      {decisionData.length === 0 ? (
        <Box>
          <Typography variant="body1">No Decision Data</Typography>
        </Box>
      ) : (
        <Box mt={2}>
          <TableContainer component={Paper} variant="outlined" square>
            <Table aria-label="Decisions table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="overline" color="textSecondary">
                      type
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="overline" color="textSecondary">
                      date
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="overline" color="textSecondary">
                      decision
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="overline" color="textSecondary">
                      max coverage
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="overline" color="textSecondary">
                      class
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="overline" color="textSecondary">
                      tobacco
                    </Typography>
                  </TableCell>
                  {productConfig?.hasDebits() && (
                    <TableCell>
                      <Typography variant="overline" color="textSecondary">
                        debits
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {decisionData.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box maxWidth="11ch" mr={1}>
                          <Typography
                            variant="body2"
                            noWrap={true}
                            title={row.decisionType}
                          >
                            {row.isAutoDecision
                              ? `${row.decisionType} (Auto)`
                              : row.decisionType}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        noWrap={true}
                        style={{ fontVariantNumeric: 'ordinal tabular-nums' }}
                      >
                        {row.createdAt}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.decision}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontVariantNumeric: 'ordinal tabular-nums' }}
                      >
                        {row.coverage}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.rateClass}</TableCell>
                    <TableCell>{row.tobaccoClass}</TableCell>
                    {productConfig?.hasDebits() && (
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{ fontVariantNumeric: 'ordinal tabular-nums' }}
                        >
                          {row.debits}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </div>
  );
};
